import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import orderBy from 'lodash.orderby';

import type { UseControlledMutation } from '@/_schemas/types';
import type { ApiError } from '@/_shared';
import { apiClient, queryClient } from '@/_shared';

import type {
  InventorCategoryCreateSchema,
  InventorCategoryQueryAllSchema,
  InventorCategoryQuerySchema,
  InventorCategoryUpdateSchema,
} from './schemas';

export const useGetInventoryCategoriesMutation: UseControlledMutation<InventorCategoryQuerySchema> = () =>
  useMutation({
    mutationFn: async ({ search, per_page }) => {
      return queryClient.fetchQuery({
        queryKey: ['INVENTORY_CATEGORIES_ASYNC', { search, per_page }],
        staleTime: Infinity,
        queryFn: async () => {
          const res = await apiClient.get<InventorCategoryQueryAllSchema>({
            endpoint: '/api/v1/inventory-categories',
            params: { search, per_page },
          });

          return orderBy(res?.data ?? [], ['name']) || [];
        },
      });
    },
  });

export function useGetInventoryCategories(apiParamStr: string) {
  return useSuspenseQuery({
    queryKey: ['INVENTORY_CATEGORIES', apiParamStr],
    queryFn: async () => {
      const res = await apiClient.get<InventorCategoryQueryAllSchema>({
        endpoint: '/api/v1/inventory-categories?include=icon',
        params: `${apiParamStr}`,
      });
      return res;
    },
  });
}

export const invalidateQueryAllObject = () => {
  queryClient.invalidateQueries({ queryKey: ['INVENTORY_CATEGORIES'] });
};

export const useCreateMutation = () =>
  useMutation<{ data: { id: number } }, ApiError, InventorCategoryCreateSchema>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: '/api/v1/inventory-categories/',
        payload,
      }),
  });

export function useGetInventoryCategory(id: string) {
  return useSuspenseQuery({
    queryKey: ['INVENTORY_CATEGORY', id],
    queryFn: async () => {
      const res = await apiClient.get<{ data: InventorCategoryQuerySchema }>({
        endpoint: `/api/v1/inventory-categories/${id}`,
        params: { include: 'icon' },
        preventAuditLog: true,
      });

      return res;
    },
  });
}

export const useUpdateMutation = (id: string) =>
  useMutation<{ data: InventorCategoryQuerySchema }, ApiError, Partial<InventorCategoryUpdateSchema>>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: `/api/v1/inventory-categories/${id}`,
        method: 'PUT',
        payload,
      }),
  });

export const useDeleteMutation = () =>
  useMutation<unknown, unknown, { id: string }>({
    mutationKey: ['INVENTORY_CATEGORY_DELETE'],
    mutationFn: ({ id }) =>
      apiClient.request({
        endpoint: `/api/v1/inventory-categories/${id}`,
        method: 'DELETE',
        payload: {},
      }),
  });
